/**
 * The screener bar's ▲▼ key — `ScreenerConfig.direction` (docs/plans/movers.md).
 *
 * All → ▲ Gainers → ▼ Losers. A hidden extra condition ANDed into the filter
 * on the way to the grid, `withRecsDirection`'s shape and its reason: the count
 * and any row limit are then taken after it, and nothing is written into the
 * stored `filter`, so turning it back to All restores the screen exactly.
 *
 * Strictly above / below zero, so a row at 0.00% shows under All only. A row
 * with no %Chg (it did not trade in the window) fails both comparisons, which
 * is right: it is neither a gainer nor a loser.
 */

import type { ScreenerConfig, ScreenerFilterNode, ScreenerSort } from "@qufolio/model";

export type Direction = NonNullable<ScreenerConfig["direction"]>;
type Filter = NonNullable<ScreenerConfig["filter"]>;

/**
 * The %Chg columns the key can read, by which session they measure. A screen
 * sorted on one of them is filtered on that one — a Pre Movers screen on
 * `prePct`, a Post Movers screen on `postPct`.
 */
export const DIRECTION_FIELDS: readonly string[] = [
  "prePct", "postPct", "changePercentCc", "changePercent",
];

/** Any other screen: the screener's own Chg % (owner, 2026-10-29). */
export const DEFAULT_DIRECTION_FIELD = "changePercent";

/** The click: All → ▲ → ▼ → All. */
export function nextDirection(direction: Direction | undefined): Direction | undefined {
  return direction === undefined ? "up" : direction === "up" ? "down" : undefined;
}

/** Which %Chg column the key filters on for this screen. */
export function directionField(sort: readonly ScreenerSort[] | undefined): string {
  const primary = sort?.[0]?.field;
  return primary && DIRECTION_FIELDS.includes(primary) ? primary : DEFAULT_DIRECTION_FIELD;
}

/** `filter` with the direction ANDed in last; unchanged under All. */
export function withDirection(
  filter: Filter | undefined,
  direction: Direction | undefined,
  field: string,
): Filter | undefined {
  if (!direction) return filter;
  const side: ScreenerFilterNode = { field, op: direction === "up" ? "gt" : "lt", value: 0 };
  if (!filter) return side;
  return { op: "all", children: [filter as ScreenerFilterNode, side] };
}

/**
 * ▼ shows the biggest losers first: a primary sort of %Chg ↓ becomes ↑.
 * Any other sort is the user's and is left alone; the stored sort never moves.
 */
export function directionSort(
  sort: ScreenerSort[] | undefined,
  direction: Direction | undefined,
  field: string,
): ScreenerSort[] | undefined {
  const primary = sort?.[0];
  if (direction !== "down" || !primary || primary.field !== field || primary.dir !== "desc") return sort;
  return [{ ...primary, dir: "asc" }, ...sort!.slice(1)];
}

/** The key's hover text. */
export function directionTitle(direction: Direction | undefined, field: string, label: string): string {
  const name = label || field;
  if (direction === "up") return `Gainers: ${name} above 0 — click for losers`;
  if (direction === "down") return `Losers: ${name} below 0 — click for all`;
  return `All rows — click for gainers (${name} above 0)`;
}
